import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId } from 'react';
import { Group, ListLayout, Virtualizer } from 'react-aria-components';
import { omit as rOmit, keys as rKeys, pickBy as rPickBy } from 'remeda';
import { nonEmpty as vNonEmpty, pipe as vPipe, string as vString } from 'valibot';

import { UPDATE_PASSWORD_URL } from '@/constants/oidc';
import { CustomerFileTypeEnum } from '@/libs/openapi';
import {
  Avatar,
  Badge,
  Button,
  ComboBox,
  Form,
  Heading,
  Hint,
  Icon,
  Image,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SegmentedButton,
  SegmentedButtonGroup,
  Text,
  TextField,
  View,
  AVATAR_SLOTS,
  TOAST_TIMEOUT,
  LIST_BOX_ITEM_SLOTS,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { fileMutations } from '@/resources/file/file.api';
import { userMutations, userQueries } from '@/resources/user/user.api';
import { Theme } from '@/types/theme.type';
import { timezone as timezoneUtil } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_form/settings/')({
  component: RouteComponent,
  staticData: {
    form: {
      heading: 'Settings',
      toolbarLayout: 'centered',
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Settings',
      },
    ],
  }),
});

function RouteComponent() {
  const id = useId();
  const routeContext = Route.useRouteContext();

  const userQuery = useQuery(userQueries.me);
  const updateMeMutation = useMutation(userMutations.updateMe);
  const fileUploadMutation = useMutation(fileMutations.upload);

  const form = useForm({
    defaultValues: {
      firstName: userQuery.data?.original?.firstName ?? '',
      lastName: userQuery.data?.original?.lastName ?? '',
      email: userQuery.data?.original?.email,
      timezone: userQuery.data?.original?.timezone ?? undefined as string | undefined,
    },
    canSubmitWhenInvalid: true,
    onSubmit: ({ value }) => updateMeMutation.mutate({
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      timezone: value.timezone,
    }),
    onSubmitInvalid: ({ value, formApi }) => {
      const validFields = rOmit(value, rKeys(rPickBy(formApi.state.fieldMeta, (field) => !field.isValid)));

      updateMeMutation.mutate({
        ...('firstName' in validFields ? { firstName: value.firstName.trim() } : {}),
        ...('lastName' in validFields ? { lastName: value.lastName.trim() } : {}),
        ...('timezone' in validFields ? { timezone: value.timezone } : {}),
      });
    },
  });

  return (
    <Form
      style={formStyles.base}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      {(renderProps) => <>
        <section {...stylex.props(sectionStyles.field)}>
          <View style={avatarStyles.view}>
            <Avatar
              isUploaded={!!routeContext.user.original.avatar?.url}
              size={104}
              onDelete={() => updateMeMutation.mutate({ avatarId: null })}
              onChange={(file) => file && fileUploadMutation.mutate(
                { type: CustomerFileTypeEnum.AVATAR, file },
                {
                  onSuccess: (data) => updateMeMutation.mutate({ avatarId: data.id }),
                  onError: (error) => routeContext.toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT }),
                },
              )}>
              {routeContext.user.original.avatar?.url ?
                <Image alt="Profile photo" src={routeContext.user.original.avatar.url} />
                : routeContext.user.meta.initials && (
                  <Text slot={routeContext.user.meta.initials.length === 2 ? AVATAR_SLOTS.TWO_LETTER_INITIALS_TEXT : AVATAR_SLOTS.ONE_LETTER_INITIALS_TEXT}>
                    {routeContext.user.meta.initials}
                  </Text>
                )}
            </Avatar>

            <View style={avatarStyles.infoView}>
              <Heading size={20} style={avatarStyles.fullNameHeading}><span {...stylex.props(avatarStyles.fullNameText)}>{routeContext.user.meta.fullName}</span><Badge color="greyLight" isPastel size={20}>{routeContext.user.transformed.role}</Badge></Heading>
              <Text color="disabled" size={14}>{routeContext.user.original.email}</Text>
            </View>
          </View>

          <form.Field
            listeners={{ onBlur: () => form.handleSubmit() }}
            name="firstName"
            validators={{ onBlur: vPipe(vString(), vNonEmpty('Enter first name')) }}>
            {(field) => (
              <TextField
                autoComplete="off"
                id={field.name}
                isInvalid={field.state.meta.errors.length > 0}
                name={field.name}
                size={48}
                style={renderProps.fieldStyles}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={field.handleChange}>
                <Label color={renderProps.labelColor}>First name</Label>
                <Input />
                {field.state.meta.errors.length > 0 && <Hint style={renderProps.underFieldStyle}><Icon />{field.state.meta.errors[0]?.message}</Hint>}
              </TextField>
            )}
          </form.Field>

          <form.Field
            listeners={{ onBlur: () => form.handleSubmit() }}
            name="lastName"
            validators={{ onBlur: vPipe(vString(), vNonEmpty('Enter last name')) }}>
            {(field) => (
              <TextField
                autoComplete="off"
                id={field.name}
                isInvalid={field.state.meta.errors.length > 0}
                name={field.name}
                size={48}
                style={renderProps.fieldStyles}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={field.handleChange}>
                <Label color={renderProps.labelColor}>Last name</Label>
                <Input />
                {field.state.meta.errors.length > 0 && <Hint style={renderProps.underFieldStyle}><Icon />{field.state.meta.errors[0]?.message}</Hint>}
              </TextField>
            )}
          </form.Field>

          <Group {...stylex.props(fieldsetStyles.base)} aria-labelledby={`${id}-password-label`}>
            <Text color={renderProps.labelColor} id={`${id}-password-label`} style={legendStyles.base}>
              Password
            </Text>
            <Group {...stylex.props(groupStyles.base, groupStyles.passwordButtons)}>
              <Button isRounded size={40} variant="quaternary" onPress={() => (window.location.href = UPDATE_PASSWORD_URL)}>
              Change
              </Button>
            </Group>
          </Group>

        </section>

        <section {...stylex.props(sectionStyles.field)}>
          <Heading level={2} size={20}>Localization</Heading>

          <form.Field
            listeners={{ onBlur: () => form.handleSubmit() }}
            name="timezone"
            validators={{ onBlur: vPipe(vString('Select timezone'), vNonEmpty('Select timezone')) }}>
            {(field) => (
              <ComboBox
                id={field.name}
                isInvalid={field.state.meta.errors.length > 0}
                name={field.name}
                selectedKey={field.state.value ? timezoneUtil.getKey(field.state.value) : null}
                size={48}
                style={renderProps.fieldStyles}
                onBlur={field.handleBlur}
                onSelectionChange={(key) => field.handleChange(key != null ? timezoneUtil.getIana(String(key)) : undefined)}>
                <Label color={renderProps.labelColor}>Timezone</Label>
                <Input placeholder="Select timezone" size={48} />
                {field.state.meta.errors.length > 0 && <Hint style={renderProps.underFieldStyle}><Icon />{field.state.meta.errors[0]?.message}</Hint>}
                <Popover>
                  <Virtualizer layout={ListLayout} layoutOptions={{ rowHeight: 40 }}>
                    <ListBox items={timezoneUtil.list()}>
                      {(item) => (
                        <ListBoxItem id={item.id} textValue={`${item.id} (${item.time})`}>
                          <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>{item.id}</Text>
                          <Text>{item.time}</Text>
                        </ListBoxItem>
                      )}
                    </ListBox>
                  </Virtualizer>
                </Popover>
              </ComboBox>
            )}
          </form.Field>
        </section>

        <section {...stylex.props(sectionStyles.field)}>
          <Heading level={2} size={20}>Personalization</Heading>

          <Group aria-labelledby={`${id}-theme-label`} {...stylex.props(fieldsetStyles.base)}>
            <Text color={renderProps.labelColor} id={`${id}-theme-label`} size={16} style={legendStyles.base}>Theme</Text>
            <SegmentedButtonGroup
              selectedKeys={[ routeContext.theme.toString() ]}
              selectionMode="single"
              size={40}
              style={segmentedButtonGroupStyles.base}
              // do not submit the form here, because it will be handled by the useSyncTheme hook
              onSelectionChange={(keys) => keys.size > 0 && routeContext.setTheme(Number(keys.keys().next().value!))}>
              <SegmentedButton id={Theme.AUTO.toString()}>Auto</SegmentedButton>
              <SegmentedButton id={Theme.LIGHT.toString()}>Light</SegmentedButton>
              <SegmentedButton id={Theme.DARK.toString()}>Dark</SegmentedButton>
            </SegmentedButtonGroup>
          </Group>
        </section>
      </>}
    </Form>
  );
}

const formStyles = stylex.create({
  base: {
    gridTemplateColumns: '1fr',
    rowGap: 80,
  },
});

const sectionStyles = stylex.create({
  field: {
    display: 'grid',
    gridTemplateColumns: {
      default: 'minmax(124px, auto) minmax(0, 405px)',
      [breakpoints.small]: '1fr',
    },
    rowGap: 24,
  },
});

const avatarStyles = stylex.create({
  view: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: 20,
    gridColumn: '1 / -1',
    maxWidth: 552,
    overflow: 'hidden',
  },
  infoView: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    justifyContent: 'center',
    minWidth: 0,
  },
  fullNameHeading: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
    overflow: 'hidden',
    width: '100%',
  },
  fullNameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const fieldsetStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    borderColor: 'transparent',
    borderStyle: 'none',
    borderWidth: 0,
    columnGap: 20,
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    margin: 0,
    minInlineSize: 'unset',
    padding: 0,
    rowGap: 16,
  },
});

const legendStyles = stylex.create({
  base: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    margin: 0,
    padding: 0,
  },
});

const segmentedButtonGroupStyles = stylex.create({
  base: {
    width: 206,
  },
});

const groupStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  passwordButtons: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
});
